import React, { useEffect, useState, useRef } from 'react';

interface ServerMetrics {
    queueCur: number;
    queueMax: number;
    queueLimit: number;
    sessionRateCur: number;
    sessionRateMax: number;
    sessionRateLimit: number;
    sessionsCur: number;
    sessionsMax: number;
    sessionsLimit: number;
    sessionsTotal: number;
    sessionsLbTot: number;
    sessionsLast: string;
    bytesIn: number;
}

interface ServerStatus {
    name: string;
    type: 'frontend' | 'backend' | 'server';
    status: 'UP' | 'DOWN';
    isBackup?: boolean;
    currentSessions: number;
    sessionRate: number;
    uptime: string;
    metrics?: ServerMetrics;
}

interface PreviousMetrics {
    [key: string]: {
        bytesIn: number;
        bytesOut: number;
        timestamp: number;
    };
}

interface HaproxyMetrics {
    currentConnections: number;
    currentConnectionRate: number;
    frontendSessionRate: number;
    servers: ServerStatus[];
    frontends: ServerStatus[];
    backends: ServerStatus[];
}

interface HaproxyCardProps {
    title?: string;
    url: string;
    username: string;
    password: string;
    knownServers: Array<{ name: string, ip: string }>;
    refreshInterval?: number;
}

const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
};

const formatUptime = (seconds: number): string => {
    if (seconds === 0) return '-';
    const days = Math.floor(seconds / (24 * 3600));
    const hours = Math.floor((seconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
};

const HaproxyCard: React.FC<HaproxyCardProps> = ({ 
    title = "HAProxy Load Balancer",
    url,
    username,
    password,
    knownServers,
    refreshInterval = 5000

}) => {
    const [metrics, setMetrics] = useState<HaproxyMetrics>({
        currentConnections: 0,
        currentConnectionRate: 0,
        frontendSessionRate: 0,
        servers: [],
        frontends: [],
        backends: []
    });
    const [error, setError] = useState<string | null>(null);
    const previousMetricsRef = useRef<PreviousMetrics>({});

    const parseMetric = (text: string, pattern: RegExp | string, defaultValue: number = 0): number => {
        const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
        const match = text.match(regex);
        return match ? parseInt(match[1]) : defaultValue;
    };

    const parseFloatMetric = (text: string, pattern: RegExp | string, defaultValue: number = 0): number => {
        const regex = typeof pattern === 'string' ? new RegExp(pattern) : pattern;
        const match = text.match(regex);
        return match ? parseFloat(match[1]) : defaultValue;
    };

    const fetchHaproxyMetrics = async () => {
        try {
            const response = await fetch(`${url}/metrics`, {
                headers: {
                    'Authorization': 'Basic ' + btoa(`${username}:${password}`)
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const text = await response.text();
            
            // Get current connections metrics
            const currentConnections = parseMetric(text, /haproxy_process_current_connections\s+(\d+)/);
            const currentConnectionRate = parseMetric(text, /haproxy_process_current_connection_rate\s+(\d+)/);
            const frontendSessionRate = parseMetric(text, /haproxy_process_current_session_rate\s+(\d+)/);

            const servers: ServerStatus[] = [];
            const frontends: ServerStatus[] = [];
            const backends: ServerStatus[] = [];

            // Find all unique proxy names in the metrics
            const proxyMatches = Array.from(text.matchAll(/haproxy_(?:frontend|backend|server)_[^{]+{proxy="([^"]+)"/g));
            const proxyNames = new Set<string>();
            for (const match of proxyMatches) {
                proxyNames.add(match[1]);
            }
            
            // Add "webservers" as fallback if no proxy names found or if it's commonly used
            if (proxyNames.size === 0 || text.includes('proxy="webservers"')) {
                proxyNames.add('webservers');
            }

            // Parse frontends
            for (const proxyName of Array.from(proxyNames)) {
                const frontendName = 'Frontend';
                const frontendStatus = parseMetric(text, new RegExp(`haproxy_frontend_status\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",frontend="${frontendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",state="UP"\\}\\s+(\\d+)`)) > 0 ? 'UP' as const : 'DOWN' as const;
                
                const frontendMetrics: ServerMetrics = {
                    queueCur: parseMetric(text, new RegExp(`haproxy_frontend_current_queue\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",frontend="${frontendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    queueMax: parseMetric(text, new RegExp(`haproxy_frontend_max_queue\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",frontend="${frontendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    queueLimit: parseMetric(text, new RegExp(`haproxy_frontend_queue_limit\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",frontend="${frontendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    sessionRateCur: parseMetric(text, new RegExp(`haproxy_frontend_current_session_rate\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",frontend="${frontendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    sessionRateMax: parseMetric(text, new RegExp(`haproxy_frontend_max_session_rate\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",frontend="${frontendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    sessionRateLimit: parseMetric(text, new RegExp(`haproxy_frontend_session_rate_limit\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",frontend="${frontendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    sessionsCur: parseMetric(text, new RegExp(`haproxy_frontend_current_sessions\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",frontend="${frontendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    sessionsMax: parseMetric(text, new RegExp(`haproxy_frontend_max_sessions\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",frontend="${frontendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    sessionsLimit: parseMetric(text, new RegExp(`haproxy_frontend_session_limit\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",frontend="${frontendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    sessionsTotal: parseMetric(text, new RegExp(`haproxy_frontend_total_sessions\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",frontend="${frontendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    sessionsLbTot: parseMetric(text, new RegExp(`haproxy_frontend_lb_total\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",frontend="${frontendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    sessionsLast: formatUptime(parseMetric(text, new RegExp(`haproxy_frontend_last_session_time\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",frontend="${frontendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`))),
                    bytesIn: parseMetric(text, new RegExp(`haproxy_frontend_bytes_in\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",frontend="${frontendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`))
                };

                if (frontendMetrics.queueCur > 0 || frontendMetrics.sessionsTotal > 0 || frontendMetrics.bytesIn > 0) {
                    frontends.push({
                        name: `${proxyName} - ${frontendName}`,
                        type: 'frontend',
                        status: frontendStatus,
                        currentSessions: frontendMetrics.sessionsCur,
                        sessionRate: frontendMetrics.sessionRateCur,
                        uptime: '-',
                        metrics: frontendMetrics
                    });
                }
            }

            // Parse backends
            for (const proxyName of Array.from(proxyNames)) {
                const backendName = 'Backend';
                const backendStatus = parseMetric(text, new RegExp(`haproxy_backend_status\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",backend="${backendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",state="UP"\\}\\s+(\\d+)`)) > 0 ? 'UP' as const : 'DOWN' as const;
                
                const backendMetrics: ServerMetrics = {
                    queueCur: parseMetric(text, new RegExp(`haproxy_backend_current_queue\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",backend="${backendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    queueMax: parseMetric(text, new RegExp(`haproxy_backend_max_queue\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",backend="${backendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    queueLimit: parseMetric(text, new RegExp(`haproxy_backend_queue_limit\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",backend="${backendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    sessionRateCur: parseMetric(text, new RegExp(`haproxy_backend_current_session_rate\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",backend="${backendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    sessionRateMax: parseMetric(text, new RegExp(`haproxy_backend_max_session_rate\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",backend="${backendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    sessionRateLimit: parseMetric(text, new RegExp(`haproxy_backend_session_rate_limit\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",backend="${backendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    sessionsCur: parseMetric(text, new RegExp(`haproxy_backend_current_sessions\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",backend="${backendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    sessionsMax: parseMetric(text, new RegExp(`haproxy_backend_max_sessions\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",backend="${backendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    sessionsLimit: parseMetric(text, new RegExp(`haproxy_backend_session_limit\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",backend="${backendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    sessionsTotal: parseMetric(text, new RegExp(`haproxy_backend_total_sessions\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",backend="${backendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    sessionsLbTot: parseMetric(text, new RegExp(`haproxy_backend_lb_total\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",backend="${backendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                    sessionsLast: formatUptime(parseMetric(text, new RegExp(`haproxy_backend_last_session_time\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",backend="${backendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`))),
                    bytesIn: parseMetric(text, new RegExp(`haproxy_backend_bytes_in\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",backend="${backendName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`))
                };

                if (backendMetrics.queueCur > 0 || backendMetrics.sessionsTotal > 0 || backendMetrics.bytesIn > 0) {
                    backends.push({
                        name: `${proxyName} - ${backendName}`,
                        type: 'backend',
                        status: backendStatus,
                        currentSessions: backendMetrics.sessionsCur,
                        sessionRate: backendMetrics.sessionRateCur,
                        uptime: '-',
                        metrics: backendMetrics
                    });
                }
            }
            
            // Parse server status for each known server (try all proxy names)
            for (const knownServer of knownServers) {
                let serverFound = false;
                for (const proxyName of Array.from(proxyNames)) {
                    // Check if this server exists in this proxy (check for any metric)
                    const serverExistsPattern = new RegExp(`haproxy_server_[^{]+\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",server="${knownServer.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"`);
                    if (text.match(serverExistsPattern)) {
                        const statusPattern = new RegExp(`haproxy_server_status\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",server="${knownServer.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",state="UP"\\}\\s+(\\d+)`);
                const statusMatch = text.match(statusPattern);
                const isUp = statusMatch ? statusMatch[1] === '1' : false;

                        const backupPattern = new RegExp(`haproxy_server_backup\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",server="${knownServer.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`);
                const backupMatch = text.match(backupPattern);
                        const isBackup = backupMatch ? backupMatch[1] === '1' : false;

                        const currentSessions = parseMetric(text, new RegExp(`haproxy_server_current_sessions\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",server="${knownServer.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`));
                        const sessionRate = parseMetric(text, new RegExp(`haproxy_server_max_session_rate\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",server="${knownServer.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`));
                        const uptime = formatUptime(Math.floor(parseFloatMetric(text, new RegExp(`haproxy_server_uptime_seconds\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",server="${knownServer.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+([\\d\.]+)`))));

                        const serverMetrics: ServerMetrics = {
                            queueCur: parseMetric(text, new RegExp(`haproxy_server_current_queue\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",server="${knownServer.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                            queueMax: parseMetric(text, new RegExp(`haproxy_server_max_queue\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",server="${knownServer.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                            queueLimit: parseMetric(text, new RegExp(`haproxy_server_queue_limit\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",server="${knownServer.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                            sessionRateCur: parseMetric(text, new RegExp(`haproxy_server_current_session_rate\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",server="${knownServer.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                            sessionRateMax: parseMetric(text, new RegExp(`haproxy_server_max_session_rate\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",server="${knownServer.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                            sessionRateLimit: parseMetric(text, new RegExp(`haproxy_server_session_rate_limit\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",server="${knownServer.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                            sessionsCur: currentSessions,
                            sessionsMax: parseMetric(text, new RegExp(`haproxy_server_max_sessions\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",server="${knownServer.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                            sessionsLimit: parseMetric(text, new RegExp(`haproxy_server_session_limit\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",server="${knownServer.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                            sessionsTotal: parseMetric(text, new RegExp(`haproxy_server_total_sessions\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",server="${knownServer.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                            sessionsLbTot: parseMetric(text, new RegExp(`haproxy_server_lb_total\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",server="${knownServer.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`)),
                            sessionsLast: formatUptime(parseMetric(text, new RegExp(`haproxy_server_last_session_time\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",server="${knownServer.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`))),
                            bytesIn: parseMetric(text, new RegExp(`haproxy_server_bytes_in\\{proxy="${proxyName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}",server="${knownServer.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}"\\}\\s+(\\d+)`))
                        };

                        servers.push({
                    name: knownServer.name,
                            type: 'server',
                    status: isUp ? 'UP' as const : 'DOWN' as const,
                            isBackup: isBackup,
                            currentSessions: currentSessions,
                            sessionRate: sessionRate,
                            uptime: uptime,
                            metrics: serverMetrics
                        });
                        serverFound = true;
                        break; // Found the server, no need to check other proxies
                    }
                }
            }

            setMetrics({ currentConnections, currentConnectionRate, frontendSessionRate, servers, frontends, backends });
            setError(null);
        } catch (err) {
            console.error(`Error fetching HAProxy metrics from ${url}:`, err);
            setError('Failed to fetch HAProxy metrics');
        }
    };

    useEffect(() => {
        fetchHaproxyMetrics();
        const intervalId = setInterval(fetchHaproxyMetrics, refreshInterval);

        return () => {
            clearInterval(intervalId);
        };
    }, [url, username, password, refreshInterval]);

    return (
        <div className="haproxy-card" style={{
            backgroundColor: 'var(--metro-card-bg)',
            border: '1px solid var(--metro-card-border)',
            borderRadius: 'var(--card-radius)',
            padding: '20px',
            marginBottom: '24px'
        }}>
            <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                marginBottom: '20px' 
            }}>
                <h3 style={{ margin: 0, color: 'var(--metro-text)' }}>
                    {title}
                </h3>
                <div style={{ 
                    display: 'flex',
                    gap: '20px',
                    color: 'var(--metro-text-secondary)',
                    fontSize: '14px'
                }}>
                    <div>Active Connections: {metrics.currentConnections}</div>
                    <div>Connection Rate: {metrics.currentConnectionRate}/s</div>
                    <div>Session Rate: {metrics.frontendSessionRate}/s</div>
                </div>
            </div>
            
            {error ? (
                <div style={{ color: 'var(--metro-error)' }}>{error}</div>
            ) : (
                <>
                    {/* Frontends */}
                    {metrics.frontends.length > 0 && (
                        <div style={{ marginBottom: '24px' }}>
                            <h4 style={{ color: 'var(--metro-text)', marginBottom: '12px', fontSize: '16px' }}>Frontends</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px' }}>
                                {metrics.frontends.map((frontend) => (
                                    <div key={frontend.name} style={{
                                        backgroundColor: 'var(--metro-dark-secondary)',
                                        borderRadius: '8px',
                                        padding: '16px'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            marginBottom: '12px'
                                        }}>
                                            <div style={{
                                                width: '8px',
                                                height: '8px',
                                                borderRadius: '50%',
                                                backgroundColor: frontend.status === 'UP' ? '#4CAF50' : '#f44336'
                                            }} />
                                            <div style={{ flex: 1, color: 'var(--metro-text)' }}>
                                                {frontend.name}
                                            </div>
                                            <div style={{
                                                color: frontend.status === 'UP' ? '#4CAF50' : '#f44336',
                                                fontWeight: 500
                                            }}>
                                                {frontend.status}
                                            </div>
                                        </div>
                                        {frontend.metrics && (
                                            <div style={{ fontSize: '12px', color: 'var(--metro-text-secondary)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                <div>Queue: {frontend.metrics.queueCur} / {frontend.metrics.queueMax || '-'}</div>
                                                <div>Session Rate: {frontend.metrics.sessionRateCur} / {frontend.metrics.sessionRateMax || '-'}</div>
                                                <div>Sessions: {frontend.metrics.sessionsCur} / {frontend.metrics.sessionsMax || '-'}</div>
                                                <div>Total: {frontend.metrics.sessionsTotal.toLocaleString()}</div>
                                                <div>LbTot: {frontend.metrics.sessionsLbTot.toLocaleString()}</div>
                                                <div>Last: {frontend.metrics.sessionsLast}</div>
                                                <div style={{ gridColumn: 'span 2' }}>Bytes In: {formatBytes(frontend.metrics.bytesIn)}</div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Backends */}
                    {metrics.backends.length > 0 && (
                        <div style={{ marginBottom: '24px' }}>
                            <h4 style={{ color: 'var(--metro-text)', marginBottom: '12px', fontSize: '16px' }}>Backends</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px' }}>
                                {metrics.backends.map((backend) => (
                                    <div key={backend.name} style={{
                                        backgroundColor: 'var(--metro-dark-secondary)',
                                        borderRadius: '8px',
                                        padding: '16px'
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                            marginBottom: '12px'
                                        }}>
                                            <div style={{
                                                width: '8px',
                                                height: '8px',
                                                borderRadius: '50%',
                                                backgroundColor: backend.status === 'UP' ? '#4CAF50' : '#f44336'
                                            }} />
                                            <div style={{ flex: 1, color: 'var(--metro-text)' }}>
                                                {backend.name}
                                            </div>
                                            <div style={{
                                                color: backend.status === 'UP' ? '#4CAF50' : '#f44336',
                                                fontWeight: 500
                                            }}>
                                                {backend.status}
                                            </div>
                                        </div>
                                        {backend.metrics && (
                                            <div style={{ fontSize: '12px', color: 'var(--metro-text-secondary)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                <div>Queue: {backend.metrics.queueCur} / {backend.metrics.queueMax || '-'}</div>
                                                <div>Session Rate: {backend.metrics.sessionRateCur} / {backend.metrics.sessionRateMax || '-'}</div>
                                                <div>Sessions: {backend.metrics.sessionsCur} / {backend.metrics.sessionsMax || '-'}</div>
                                                <div>Total: {backend.metrics.sessionsTotal.toLocaleString()}</div>
                                                <div>LbTot: {backend.metrics.sessionsLbTot.toLocaleString()}</div>
                                                <div>Last: {backend.metrics.sessionsLast}</div>
                                                <div style={{ gridColumn: 'span 2' }}>Bytes In: {formatBytes(backend.metrics.bytesIn)}</div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Known Servers */}
                    {metrics.servers.length > 0 && (
                        <div>
                            <h4 style={{ color: 'var(--metro-text)', marginBottom: '12px', fontSize: '16px' }}>Servers</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '12px' }}>
                    {knownServers.map((knownServer) => {
                        const server = metrics.servers.find(s => s.name === knownServer.name);
                        const isDown = !server || server.status === 'DOWN';
                        return (
                            <div key={knownServer.name} style={{
                                backgroundColor: 'var(--metro-dark-secondary)',
                                borderRadius: '8px',
                                padding: '16px'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    marginBottom: '12px'
                                }}>
                                    <div style={{
                                        width: '8px',
                                        height: '8px',
                                        borderRadius: '50%',
                                        backgroundColor: !isDown ? '#4CAF50' : '#f44336'
                                    }} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ color: 'var(--metro-text)' }}>
                                            {knownServer.name}
                                            {knownServer.ip && (
                                                <span style={{ 
                                                    marginLeft: '8px',
                                                    fontSize: '12px',
                                                    color: 'var(--metro-text-secondary)'
                                                }}>
                                                    {knownServer.ip}
                                                </span>
                                            )}  
                                            <span style={{ 
                                                marginLeft: '8px',
                                                fontSize: '12px',
                                                color: 'var(--metro-text-secondary)'
                                            }}>
                                                {(server && server.isBackup) ? '(backup)' : '(primary)'}
                                            </span>
                                        </div>
                                    </div>
                                    <div style={{
                                        color: !isDown ? '#4CAF50' : '#f44336',
                                        fontWeight: 500
                                    }}>
                                        {!isDown ? 'UP' : 'DOWN'}
                                    </div>
                                </div>
                                            {server && server.metrics && (
                                                <div style={{ fontSize: '12px', color: 'var(--metro-text-secondary)', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                                    <div>Queue: {server.metrics.queueCur} / {server.metrics.queueMax || '-'}</div>
                                                    <div>Session Rate: {server.metrics.sessionRateCur} / {server.metrics.sessionRateMax || '-'}</div>
                                                    <div>Sessions: {server.metrics.sessionsCur} / {server.metrics.sessionsMax || '-'}</div>
                                                    <div>Total: {server.metrics.sessionsTotal.toLocaleString()}</div>
                                                    <div>LbTot: {server.metrics.sessionsLbTot.toLocaleString()}</div>
                                                    <div>Last: {server.metrics.sessionsLast}</div>
                                                    <div>Uptime: {server.uptime}</div>
                                                    <div>Bytes In: {formatBytes(server.metrics.bytesIn)}</div>
                                                </div>
                                            )}
                            </div>
                        );
                    })}
                </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default HaproxyCard;